import React, { useContext, useEffect, useRef, useState } from "react";
import {login, loginCodeRust} from "../http/userAPI";
import { useNavigate } from "react-router-dom";
import { HOME_ROUTE } from "../utils/consts";
import { Context } from "..";
import { observer } from "mobx-react-lite";
import {getApiErrorMessage} from "../http/apiError";
import {
    Box,
    Button,
    IconButton,
    TextField,
    Typography,
    Divider
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CSSTransition } from "react-transition-group";
import * as THREE from "three";
import "./Login.css";

const Login = observer(() => {
    const { user } = useContext(Context);
    const navigate = useNavigate();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");

    const [authMode, setAuthMode] = useState("password"); // password | rust
    const [rustCode, setRustCode] = useState("");

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showForm, setShowForm] = useState(false);

    const formRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        setTimeout(() => setShowForm(true), 50);
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (!isMobile && formRef.current && !formRef.current.contains(e.target)) {
                setShowForm(false);
                setTimeout(() => navigate(-1), 300);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isMobile, navigate]);

    const loginPassword = async () => {
        try {
            const data = await login(username, password);
            user.setUser(data);
            navigate(HOME_ROUTE);
        } catch (e) {
            console.error(getApiErrorMessage(e, 'Ошибка авторизации'));
        }
    };

    const loginRust = async () => {
        try {
            const data = await loginCodeRust(rustCode);
            user.setUser(data);
            navigate(HOME_ROUTE);
        } catch (e) {
            console.error(getApiErrorMessage(e, 'Ошибка авторизации'));
        }
    };

    const goBack = () => {
        setShowForm(false);
        setTimeout(() => navigate(-1), 300);
    };

    /* ================= 3D BACKGROUND ================= */

    useEffect(() => {
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );

        const renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            alpha: true
        });

        renderer.setSize(window.innerWidth, window.innerHeight);
        camera.position.z = 5;

        const createCircleTexture = (color) => {
            const size = 64;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d");

            ctx.fillStyle = color.getStyle();
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
            ctx.fill();

            return new THREE.CanvasTexture(canvas);
        };

        const getRandomColor = () =>
            new THREE.Color(
                THREE.MathUtils.randFloat(0.2, 1),
                THREE.MathUtils.randFloat(0.2, 1),
                1
            );

        const geometry = new THREE.BufferGeometry();
        const vertices = [];

        for (let i = 0; i < 20; i++) {
            vertices.push(
                THREE.MathUtils.randFloatSpread(200),
                THREE.MathUtils.randFloatSpread(2),
                THREE.MathUtils.randFloatSpread(200)
            );

            const color = getRandomColor();
            const material = new THREE.PointsMaterial({
                color,
                size: 0.7,
                transparent: true,
                opacity: 0.7,
                blending: THREE.AdditiveBlending,
                map: createCircleTexture(color)
            });

            const star = new THREE.Points(geometry, material);
            scene.add(star);
        }

        geometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(vertices, 3)
        );

        let mouseX = 0;
        let mouseY = 0;

        const onMouseMove = (e) => {
            mouseX = (e.clientX / window.innerWidth) * 2 - 1;
            mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
        };

        document.addEventListener("mousemove", onMouseMove);

        const animate = () => {
            scene.children.forEach((s) => {
                s.rotation.x += 0.0006;
                s.rotation.y += 0.0006;
            });

            camera.position.x += (mouseX * 0.3 - camera.position.x) * 0.04;
            camera.position.y += (mouseY * 0.3 - camera.position.y) * 0.04;
            camera.lookAt(scene.position);

            renderer.render(scene, camera);
            requestAnimationFrame(animate);
        };

        animate();

        const onResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };

        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
            document.removeEventListener("mousemove", onMouseMove);
        };
    }, []);

    /* ================= RENDER ================= */

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isMobile ? "background.default" : "rgba(0,0,0,1)",
                zIndex: 1300
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1
                }}
            />

            <CSSTransition in={showForm} timeout={300} classNames="fade" unmountOnExit>
                <Box
                    ref={formRef}
                    sx={{
                        width: isMobile ? "100%" : 420,
                        bgcolor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        boxShadow: isMobile ? "none" : 6,
                        position: "relative",
                        zIndex: 2
                    }}
                >
                    <IconButton
                        onClick={goBack}
                        sx={{ position: "absolute", top: 20, right: 20 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    <Typography variant="h5" gutterBottom>
                        Авторизация
                    </Typography>

                    {authMode === "password" && (
                        <>
                            <TextField
                                fullWidth
                                label="Логин"
                                margin="normal"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />

                            <TextField
                                fullWidth
                                label="Пароль"
                                type="password"
                                margin="normal"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={loginPassword}
                            >
                                Войти
                            </Button>

                            <Divider sx={{ my: 2 }}>или</Divider>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setAuthMode("rust")}
                            >
                                Войти через Rust
                            </Button>
                        </>
                    )}

                    {authMode === "rust" && (
                        <>
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: "background.default",
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="subtitle2" gutterBottom>
                                    Авторизация через игру Rust
                                </Typography>
                                <Typography variant="body2">
                                    1. Зайдите на сервер Rust<br />
                                    2. Введите команду <b>/login</b><br />
                                    3. Получите шестизначный код<br />
                                    4. Введите его ниже в течение <b>30 минут</b><br />
                                    5. Если код истёк — запросите новый в игре
                                </Typography>
                            </Box>

                            <TextField
                                fullWidth
                                label="Код из игры"
                                margin="normal"
                                value={rustCode}
                                onChange={(e) => setRustCode(e.target.value)}
                                autoComplete="off"
                                inputProps={{
                                    maxLength: 6,
                                    autoComplete: "one-time-code",
                                    inputMode: "numeric",
                                    pattern: "[0-9]*"
                                }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={loginRust}
                            >
                                Подтвердить код
                            </Button>

                            <Button
                                fullWidth
                                variant="text"
                                sx={{ mt: 1 }}
                                onClick={() => setAuthMode("password")}
                            >
                                Назад к логину и паролю
                            </Button>
                        </>
                    )}
                </Box>
            </CSSTransition>
        </Box>
    );
});

export default Login;
